import { Button, IconButton, Input, Typography, Dialog, Menu } from "@material-tailwind/react";
import { HiOutlinePlus, HiSearch, HiX, HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineDotsHorizontal, HiTrash, HiOutlineExclamationCircle } from "react-icons/hi";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import TradenameAPI from '../api/tradename';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import { useDialog } from '../context/DialogContext';
import PaginationCustom from '../components/molecules/PaginationCustom';

const SkeletonRow = () => (
  <div className="w-full h-12 rounded bg-primary/5 animate-pulse"></div>
);

const Tradename = () => {
  const [tradenames, setTradenames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    totalItems: 0,
    totalPages: 0
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tradenameToDelete, setTradenameToDelete] = useState(null);
  const navigate = useNavigate();
 // Get dialog functions from context
  const { openTradenameDialog } = useDialog();

  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const currentLimit = Math.max(10, parseInt(searchParams.get('limit') || '50', 10) || 50);

  const debouncedSearch = useCallback(
    debounce((keyword, page, limit) => {
      if (keyword) {
        searchTradenames(keyword, page, limit);
      } else {
        fetchTradenames(page, limit);
      }
    }, 500),
    []
  );

  useEffect(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    const search = searchParams.get('search') || '';
    const limit = currentLimit;
    setSearchKeyword(search);
    debouncedSearch(search, page, limit);
  }, [searchParams]);

  const handleSearch = (e) => {
    const keyword = e.target.value;
    setSearchKeyword(keyword);
    setSearchParams({ page: '1', search: keyword, limit: currentLimit.toString() });
  };

  const handlePageChange = (newPage) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('page', newPage.toString());
      params.set('limit', currentLimit.toString());
      return params;
    });
  };

  const handleSizeChange = (newSize) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('page', '1');
      params.set('limit', newSize.toString());
      return params;
    });
  };

  const searchTradenames = async (keyword, page, limit) => {
    try {
      setLoading(true);
      const response = await TradenameAPI.search(keyword, page, limit);
      setTradenames(response.results);
      setPagination({
        page: response.pagination?.page || 1,
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 0
      });
    } catch (error) {
      toast.error('Failed to search tradenames');
    } finally {
      setLoading(false);
    }
  };

  const fetchTradenames = async (page , limit) => {
    try {
      setLoading(true);

      const response = await TradenameAPI.getByPage(page ?? currentPage, limit ?? currentLimit);
      setTradenames(response.results);
      setPagination({
        page: response.pagination?.page || 1,
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 0
      });
    } catch (error) {
      toast.error('Failed to fetch tradenames');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tradename) => {
    setTradenameToDelete(tradename);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await TradenameAPI.delete(tradenameToDelete._id);
      if (response) {
        toast.success('Tradename deleted successfully');
        setDeleteDialogOpen(false);
        setTradenameToDelete(null);

        if (searchKeyword) {
          searchTradenames(searchKeyword, currentPage, currentLimit);
        } else {
          fetchTradenames(currentPage, currentLimit);
        }
      }
    } catch (error) {
      toast.error('Failed to delete tradename');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (tradenameUri) => {
    openTradenameDialog({
      mode: 'edit',
      tradenameUri: tradenameUri,
      onSuccess: (updatedTradename) => {
        if (searchKeyword) {
          searchTradenames(searchKeyword, currentPage, currentLimit);
        } else {
          fetchTradenames();
        }
        toast.success(`Tradename ${updatedTradename.name} updated successfully`);
      }
    });
  };

  const handleCreateClick = () => {
    openTradenameDialog({
      mode: 'create',
      onSuccess: (newTradename) => {
        fetchTradenames(); // Refresh the list after creation
        toast.success(`Tradename ${newTradename.name} created successfully`);
      }
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="w-full max-w-[300px]">
          <Input
            className="bg-white"
            placeholder="Search by name"
            disabled={loading}
            value={searchKeyword}
            onChange={handleSearch}
          >
            <Input.Icon>
              <HiSearch className="size-full" />
            </Input.Icon>
          </Input>
        </div>
        <Button
          className="flex items-center gap-2 px-4"
          disabled={loading}
          onClick={handleCreateClick}
        >
          <HiOutlinePlus className="w-4 h-4" /> New Tradename
        </Button>
      </div>

      {!loading && pagination.totalItems > 0 && (
          <PaginationCustom
            currentPage={currentPage}
            pageSize={currentLimit}
            totalItems={pagination.totalItems}
            onPageChange={handlePageChange}
            onSizeChange={handleSizeChange}
            loading={loading}
          />
        )}
        
      <div className="grid grid-cols-1 gap-2 mb-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {loading ? (
          Array.from({ length: 20 }).map((_, index) => (
            <SkeletonRow key={index} />
          ))
        ) : tradenames.map((tradename) => (
          <div
            key={tradename._id}
            onClick={() => handleEditClick(tradename.uri)}
            className="relative h-full px-4 py-3 leading-none transition bg-white border rounded-md shadow-none cursor-pointer text-primary hover:border-primary/50 group"
          >
            <div className="flex items-center justify-between">
              <span className="break-words text-14">{tradename.name}</span>
              {tradename.status === 'inactive' && (
                <span className="px-2 py-0.5 text-xs font-medium text-blue-gray-600 bg-blue-gray-50 border border-blue-gray-100 rounded-full">
                  Inactive
                </span>
              )}
            </div>
            <div className="absolute hidden -top-2 -right-2 group-hover:block">
              <IconButton
                size="sm"
                className="min-w-0 min-h-0 duration-150 border rounded-full size-5 border-primary hover:bg-red-500 hover:text-white hover:border-red-500"
                color="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete(tradename);
                }}
              >
                <HiX className="size-3" />
              </IconButton>
            </div>
          </div>
        ))}
        {!loading && tradenames.length === 0 && (
          <div className="flex flex-col items-center justify-center w-full h-full gap-5 col-span-full">
            <HiOutlineExclamationCircle className="opacity-50 size-12 text-blue-gray-400" />
            <Typography className="font-normal text-blue-gray-400">
              {searchKeyword ? 'No tradenames found matching your search' : 'No tradenames found'}
            </Typography>
            {!searchKeyword && (
              <Button
                className="flex items-center gap-2 px-4"
                onClick={handleCreateClick}
              >
                <HiOutlinePlus className="w-4 h-4" /> Create Tradename
              </Button>
            )}
          </div>
        )}
      </div>


      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        handler={() => setDeleteDialogOpen(false)}
        size="sm"
      >
        <Dialog.Overlay>
          <Dialog.Content>
            <div className="p-0">
              <div className="p-4 text-center">
                <Typography variant="h5" className="mb-4">
                  Delete Tradename
                </Typography>
                <Typography>
                  Are you sure you want to delete "{tradenameToDelete?.name}"?<br />
                  This action cannot be undone.
                </Typography>
              </div>
              <div className="flex justify-center gap-4 p-4 border-t">
                <Button
                  onClick={() => setDeleteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog>
    </div>
  );
};

export default Tradename; 